import React, { useEffect } from 'react'
import { EditionPublication } from '@west-australian-newspapers/message-types'
import {
    AllEvents,
    createEditionSelectItemEvent,
    createEditionViewItemListEvent,
    Product,
    SpecialEditionType,
    WebLink,
} from '@news-mono/web-common'
import { NightlyButton } from '../../../buttons/NightlyButton/NightlyButton'
import {
    StyledNightlyEditionContainer,
    StyledNightlyEditionContent,
    StyledNightlyEditionPicture,
    StyledNightlyEditionHeading,
    StyledNightlyEditionTextContainer,
    StyledNightlyEditionSubHeading,
    StyledNightlyEditionTeaser,
    StyledNightlyEditionButtonContainer,
    Divider,
} from '../SpecificEdition/NightlySpecificEdition.styled'
import { HeaderContainer, HeaderPrefix } from './SpecialEdition.styled'

export interface SpecialEditionProps {
    title: string
    teaser: string
    link: string
    imageURL: string
    lazyloadImages?: boolean
    shouldShowAllEditions?: boolean
    alt: string
    publicationDate: string
    path: string
    onEvent?: (event: AllEvents) => void
    editionId?: string
    editionType?: EditionPublication
    allEditionsLink?: string
    subHeading?: string
}

export const SpecialEdition = ({
    title,
    teaser,
    link,
    imageURL,
    lazyloadImages,
    shouldShowAllEditions = false, // if false, it will show ReadEditionButton instead
    alt,
    path,
    onEvent,
    editionId,
    subHeading = 'In this Edition',
    editionType = 'the_nightly_on', // default to 'the_nightly_on'
    allEditionsLink = '/on',
}: SpecialEditionProps) => {
    const isTravelEdition = editionType === 'the_nightly_travel'
    const editionTypeCapitalized = isTravelEdition ? 'Travel' : 'On'
    const editionTypeLowerCase =
        editionTypeCapitalized.toLowerCase() as SpecialEditionType
    useEffect(() => {
        if (shouldShowAllEditions) {
            return
        }
        const event = createEditionViewItemListEvent({
            editions: [
                {
                    id: editionId,
                    heading: teaser,
                    title: title,
                },
            ],
            collectionName: `thenightly_${editionTypeLowerCase}_home_page`,
            itemCategory: 'thenightly_specific_edition_page',
        })

        if (onEvent && event) {
            onEvent(event)
        }
    }, [
        shouldShowAllEditions,
        editionId,
        teaser,
        title,
        onEvent,
        editionTypeLowerCase,
    ])

    const handleClick = () => {
        onEvent &&
            onEvent(
                createEditionSelectItemEvent({
                    edition: {
                        id: editionId,
                        heading: title,
                    },
                    collectionName: `TheNightly${editionTypeCapitalized}_SpecialEditionImage`,
                    product: Product.TheNightly,
                    itemDetails: {
                        category: shouldShowAllEditions
                            ? `thenightly_${editionTypeLowerCase}_specific_edition_page`
                            : `thenightly_${editionTypeLowerCase}_home_page`,

                        linkText: 'SpecialEditionImageClicked',
                        linkUrl: link,
                    },
                }),
            )
    }

    return (
        <>
            <StyledNightlyEditionContainer>
                <StyledNightlyEditionContent>
                    <StyledNightlyEditionPicture>
                        <WebLink to={link} onClick={handleClick}>
                            <img
                                loading={lazyloadImages ? 'lazy' : undefined}
                                src={imageURL}
                                onError={({ currentTarget }) => {
                                    currentTarget.onerror = null
                                    currentTarget.src = require('../../ePaper/assets/paper.jpg')
                                }}
                                alt={alt}
                            />
                        </WebLink>
                    </StyledNightlyEditionPicture>
                    <HeaderContainer>
                        {!isTravelEdition && (
                            <HeaderPrefix>
                                The Nightly {editionTypeCapitalized}
                            </HeaderPrefix>
                        )}
                        <StyledNightlyEditionHeading
                            as={shouldShowAllEditions ? 'h1' : 'h2'}
                        >
                            {isTravelEdition ? title.toUpperCase() : title}
                        </StyledNightlyEditionHeading>
                        {isTravelEdition && (
                            <HeaderPrefix>By The Nightly</HeaderPrefix>
                        )}
                    </HeaderContainer>
                    <StyledNightlyEditionTextContainer>
                        <StyledNightlyEditionSubHeading>
                            {subHeading}
                        </StyledNightlyEditionSubHeading>
                        <StyledNightlyEditionTeaser>
                            {teaser}
                        </StyledNightlyEditionTeaser>
                    </StyledNightlyEditionTextContainer>
                    <StyledNightlyEditionButtonContainer>
                        <NightlyButton
                            variant={'default'}
                            text={'Read Edition'}
                            action={{
                                type: 'link',
                                to: link,
                                onClick: () =>
                                    onEvent &&
                                    onEvent(
                                        createEditionSelectItemEvent({
                                            edition: {
                                                id: editionId,
                                                heading: title,
                                            },
                                            collectionName: `TheNightly${editionTypeCapitalized}_ReadEditionButton`,
                                            product: Product.TheNightly,
                                            itemDetails: {
                                                category: shouldShowAllEditions
                                                    ? `thenightly_${editionTypeLowerCase}_specific_edition_page`
                                                    : `thenightly_${editionTypeLowerCase}_home_page`,

                                                linkText: `Read Edition`,
                                                linkUrl: link,
                                            },
                                        }),
                                    ),
                            }}
                            color={'primary'}
                            fill={'filled'}
                            layout={{
                                shouldGrow: true,
                                alignSelf: 'stretch',
                            }}
                        />
                        {shouldShowAllEditions ? (
                            <NightlyButton
                                variant={'default'}
                                text={'All Editions'}
                                action={{
                                    type: 'link',
                                    to: allEditionsLink,
                                    onClick: () =>
                                        onEvent &&
                                        onEvent(
                                            createEditionSelectItemEvent({
                                                edition: {
                                                    id: editionId,
                                                    heading: title,
                                                },
                                                collectionName: `TheNightly${editionTypeCapitalized}_AllEditionsButton`,
                                                product: Product.TheNightly,
                                                itemDetails: {
                                                    category: `thenightly_${editionTypeLowerCase}_specific_edition_page`,
                                                    linkText: 'All Editions',
                                                    linkUrl: allEditionsLink,
                                                },
                                            }),
                                        ),
                                }}
                                color={'primary'}
                                fill={'outline'}
                                layout={{
                                    shouldGrow: true,
                                    alignSelf: 'stretch',
                                }}
                            />
                        ) : (
                            <NightlyButton
                                variant={'default'}
                                text={'More Articles'}
                                action={{
                                    type: 'link',
                                    to: path,
                                    onClick: () =>
                                        onEvent &&
                                        onEvent(
                                            createEditionSelectItemEvent({
                                                edition: {
                                                    id: editionId,
                                                    heading: title,
                                                },
                                                collectionName: `TheNightly${editionTypeCapitalized}_MoreArticlesButton`,
                                                product: Product.TheNightly,
                                                itemDetails: {
                                                    category: `thenightly_${editionTypeLowerCase}_home_page`,
                                                    linkText: 'More Articles',
                                                    linkUrl: path,
                                                },
                                            }),
                                        ),
                                }}
                                color={'primary'}
                                fill={'outline'}
                                layout={{
                                    shouldGrow: true,
                                    alignSelf: 'stretch',
                                }}
                            />
                        )}
                    </StyledNightlyEditionButtonContainer>
                    <StyledNightlyEditionTextContainer></StyledNightlyEditionTextContainer>
                </StyledNightlyEditionContent>
            </StyledNightlyEditionContainer>

            <Divider />
        </>
    )
}
